import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography, Container, Grid, Paper, Box,
  IconButton, CircularProgress, Divider, Chip, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Select, MenuItem,
  FormControl, InputLabel,
} from '@mui/material';
import {
  Refresh,
  People, Hotel, Description, CurrencyRupee, LocalHospital,
} from '@mui/icons-material';
import TopBar from '../../../components/TopBar';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, Cell, ResponsiveContainer,
} from 'recharts';
import { doctorPerformanceService, userService } from '../../../services/api';

// â”€â”€ helpers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const toInputDate = (d) => d.toISOString().split('T')[0];

const fmtCurrency = (n) =>
  '₹ ' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const STATUS_COLORS = { Completed: '#2e7d32', Scheduled: '#1565c0', Cancelled: '#c62828' };
const TYPE_COLORS   = { OPD: '#1565c0', IPD: '#9c27b0' };
const PALETTE       = ['#1565c0', '#9c27b0', '#2e7d32', '#e65100', '#c62828', '#00695c'];

const QUICK_RANGES = [
  { label: 'Today',        key: 'today'  },
  { label: 'This Week',    key: 'week'   },
  { label: 'This Month',   key: 'month'  },
  { label: 'Last 30 Days', key: 'last30' },
];

const getQuickRange = (key) => {
  const today = new Date();
  const fmt   = (d) => toInputDate(d);
  switch (key) {
    case 'today': return { from: fmt(today), to: fmt(today) };
    case 'week': {
      const d = new Date(today);
      d.setDate(today.getDate() - (today.getDay() === 0 ? 6 : today.getDay() - 1));
      return { from: fmt(d), to: fmt(today) };
    }
    case 'month':
      return { from: fmt(new Date(today.getFullYear(), today.getMonth(), 1)), to: fmt(today) };
    case 'last30':
    default: {
      const d = new Date(today);
      d.setDate(today.getDate() - 29);
      return { from: fmt(d), to: fmt(today) };
    }
  }
};

// â”€â”€ sub-components â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const SummaryCard = ({ title, value, color, icon }) => (
  <Paper elevation={2} sx={{ p: 2.5, borderRadius: 2, borderLeft: `4px solid ${color}`, height: '100%' }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>{title}</Typography>
        <Typography variant="h5" sx={{ fontWeight: 700, color }}>{value}</Typography>
      </Box>
      <Box sx={{ color, opacity: 0.6 }}>{icon}</Box>
    </Box>
  </Paper>
);

const ChartCard = ({ title, height = 300, children }) => (
  <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>{title}</Typography>
    <Box sx={{ height }}>{children}</Box>
  </Paper>
);

const EmptyState = ({ message = 'No data for selected period' }) => (
  <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <Typography variant="body2" color="text.secondary">{message}</Typography>
  </Box>
);

const renderPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  if (percent < 0.05) return null;
  const R = Math.PI / 180;
  const r = innerRadius + (outerRadius - innerRadius) * 0.55;
  const x = cx + r * Math.cos(-midAngle * R);
  const y = cy + r * Math.sin(-midAngle * R);
  return (
    <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central"
      fontSize={12} fontWeight={700}>
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

const CountTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <Paper sx={{ p: 1.5, border: '1px solid #e0e0e0', minWidth: 140 }}>
      {label && (
        <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, mb: 0.5 }}>
          {label}
        </Typography>
      )}
      {payload.map((p, i) => (
        <Typography key={i} variant="caption" sx={{ display: 'block', color: p.fill || p.color }}>
          {p.name}: {p.value}
        </Typography>
      ))}
    </Paper>
  );
};

// â”€â”€ Main component â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const DoctorPerformanceDashboard = () => {
  const navigate = useNavigate();

  const init = getQuickRange('last30');
  const [dateFrom,         setDateFrom]         = useState(init.from);
  const [dateTo,           setDateTo]           = useState(init.to);
  const [activeQuick,      setActiveQuick]      = useState('last30');
  const [selectedDoctorId, setSelectedDoctorId] = useState('');
  const [refreshKey,       setRefreshKey]       = useState(0);
  const [loading,          setLoading]          = useState(false);

  const [doctors,          setDoctors]          = useState([]);
  const [summary,          setSummary]          = useState(null);
  const [trend,            setTrend]            = useState([]);
  const [patientTypes,     setPatientTypes]     = useState([]);
  const [statusBreakdown,  setStatusBreakdown]  = useState([]);
  const [topMedicines,     setTopMedicines]     = useState([]);
  const [comparison,       setComparison]       = useState([]);
  const [recentAppts,      setRecentAppts]      = useState([]);

  // Load doctors list once
  useEffect(() => {
    userService.getDoctors()
      .then((r) => setDoctors(r.data))
      .catch(() => {});
  }, []);

  // Load analytics whenever filters change
  useEffect(() => {
    let cancelled = false;
    const fetchAll = async () => {
      setLoading(true);
      const docId = selectedDoctorId || undefined;

      const results = await Promise.allSettled([
        doctorPerformanceService.getSummary(docId, dateFrom, dateTo),
        doctorPerformanceService.getTrend(docId, dateFrom, dateTo),
        doctorPerformanceService.getPatientTypes(docId, dateFrom, dateTo),
        doctorPerformanceService.getStatusBreakdown(docId, dateFrom, dateTo),
        doctorPerformanceService.getTopMedicines(docId, dateFrom, dateTo),
        doctorPerformanceService.getComparison(dateFrom, dateTo),
        doctorPerformanceService.getRecentAppointments(docId, dateFrom, dateTo),
      ]);

      if (cancelled) return;

      const [sumR, trendR, ptR, sbR, tmR, compR, recentR] = results;
      if (sumR.status    === 'fulfilled') setSummary(sumR.value.data);
      if (trendR.status  === 'fulfilled') setTrend(trendR.value.data);
      if (ptR.status     === 'fulfilled') setPatientTypes(ptR.value.data);
      if (sbR.status     === 'fulfilled') setStatusBreakdown(sbR.value.data);
      if (tmR.status     === 'fulfilled') setTopMedicines(tmR.value.data);
      if (compR.status   === 'fulfilled') setComparison(compR.value.data);
      if (recentR.status === 'fulfilled') setRecentAppts(recentR.value.data);

      if (!cancelled) setLoading(false);
    };
    fetchAll();
    return () => { cancelled = true; };
  }, [selectedDoctorId, dateFrom, dateTo, refreshKey]);

  const handleQuick = (key) => {
    const r = getQuickRange(key);
    setDateFrom(r.from);
    setDateTo(r.to);
    setActiveQuick(key);
  };

  // Shape data for Recharts
  const trendData = trend.map((t) => ({
    label: new Date(t.date + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }),
    OPD:   t.opd,
    IPD:   t.ipd,
  }));

  const ptData  = patientTypes.map((p)    => ({ name: p.type,     value: p.count }));
  const sbData  = statusBreakdown.map((s) => ({ name: s.status,   value: s.count }));
  const medData = topMedicines.map((m)    => ({ medicine: m.medicine, Count: m.count }));

  const selectedDoctor = doctors.find((d) => String(d.id) === String(selectedDoctorId));

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">

      {/* â”€â”€ AppBar â”€â”€ */}
      <TopBar
        title="Doctor Performance Dashboard"
        onBack={() => navigate(-1)}
        actions={
          <>
            {loading && <CircularProgress size={22} color="inherit" sx={{ mr: 2 }} />}
            <IconButton color="inherit" title="Refresh" onClick={() => setRefreshKey((k) => k + 1)}>
              <Refresh />
            </IconButton>
          </>
        }
      />

      <Container maxWidth="xl" sx={{ py: 3 }}>

        {/* â”€â”€ Filter Bar â”€â”€ */}
        <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>

            <FormControl size="small" sx={{ minWidth: 240 }}>
              <InputLabel>Doctor</InputLabel>
              <Select
                value={selectedDoctorId}
                label="Doctor"
                onChange={(e) => setSelectedDoctorId(e.target.value)}
              >
                <MenuItem value="">All Doctors</MenuItem>
                {doctors.map((d) => (
                  <MenuItem key={d.id} value={String(d.id)}>
                    Dr. {d.firstName} {d.lastName}
                    {d.specialization ? ` — ${d.specialization}` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Divider orientation="vertical" flexItem />

            <Typography variant="body2" sx={{ fontWeight: 600 }}>Period:</Typography>
            {QUICK_RANGES.map((qr) => (
              <Chip
                key={qr.key}
                label={qr.label}
                size="small"
                onClick={() => handleQuick(qr.key)}
                color={activeQuick === qr.key ? 'primary' : 'default'}
                variant={activeQuick === qr.key ? 'filled' : 'outlined'}
              />
            ))}

            <Divider orientation="vertical" flexItem />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2">From:</Typography>
              <input
                type="date"
                value={dateFrom}
                max={dateTo}
                onChange={(e) => { setDateFrom(e.target.value); setActiveQuick(null); }}
                style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #bbb', fontSize: 13 }}
              />
              <Typography variant="body2">To:</Typography>
              <input
                type="date"
                value={dateTo}
                min={dateFrom}
                max={toInputDate(new Date())}
                onChange={(e) => { setDateTo(e.target.value); setActiveQuick(null); }}
                style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #bbb', fontSize: 13 }}
              />
            </Box>
          </Box>

          {selectedDoctor && (
            <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid #e0e0e0', display: 'flex', gap: 1, alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">Viewing:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Dr. {selectedDoctor.firstName} {selectedDoctor.lastName}
              </Typography>
              {selectedDoctor.specialization && (
                <Chip label={selectedDoctor.specialization} size="small" color="primary" variant="outlined" />
              )}
            </Box>
          )}
        </Paper>

        {/* â”€â”€ 1. KPI Cards â”€â”€ */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { title: 'Total Patients Seen',   value: summary?.totalPatients      ?? 0,  color: '#1565c0', icon: <People       sx={{ fontSize: 34 }} /> },
            { title: 'OPD Patients',          value: summary?.totalOpd           ?? 0,  color: '#2e7d32', icon: <LocalHospital sx={{ fontSize: 34 }} /> },
            { title: 'IPD Patients',          value: summary?.totalIpd           ?? 0,  color: '#9c27b0', icon: <Hotel        sx={{ fontSize: 34 }} /> },
            { title: 'Prescriptions Written', value: summary?.totalPrescriptions ?? 0,  color: '#e65100', icon: <Description  sx={{ fontSize: 34 }} /> },
            { title: 'Revenue Generated',     value: fmtCurrency(summary?.revenue),     color: '#2e7d32', icon: <CurrencyRupee sx={{ fontSize: 34 }} /> },
          ].map((card) => (
            <Grid item xs={12} sm={6} md={4} lg={2.4} key={card.title}>
              <SummaryCard {...card} />
            </Grid>
          ))}
        </Grid>

        {/* â”€â”€ 2. Appointments Trend â”€â”€ */}
        <Box sx={{ mb: 3 }}>
          <ChartCard title="Appointments Trend (Completed)" height={300}>
            {trendData.length === 0 ? <EmptyState /> : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={trendData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip content={<CountTooltip />} />
                  <Legend />
                  <Bar dataKey="OPD" fill={TYPE_COLORS.OPD} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="IPD" fill={TYPE_COLORS.IPD} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </Box>

        {/* â”€â”€ 3 & 4. Patient Type + Status Breakdown â”€â”€ */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <ChartCard title="Patient Type Breakdown" height={290}>
              {ptData.length === 0 ? <EmptyState /> : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={ptData} dataKey="value" nameKey="name"
                      cx="50%" cy="50%" outerRadius={100}
                      labelLine={false} label={renderPieLabel}>
                      {ptData.map((e, i) => (
                        <Cell key={i} fill={TYPE_COLORS[e.name] || PALETTE[i % PALETTE.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v, name) => [v, name]} />
                    <Legend formatter={(name) => {
                      const d = ptData.find((p) => p.name === name);
                      return `${name} (${d?.value ?? 0} patients)`;
                    }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <ChartCard title="Appointment Status Breakdown" height={290}>
              {sbData.length === 0 ? <EmptyState /> : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={sbData} dataKey="value" nameKey="name"
                      cx="50%" cy="50%" outerRadius={100}
                      labelLine={false} label={renderPieLabel}>
                      {sbData.map((e, i) => (
                        <Cell key={i} fill={STATUS_COLORS[e.name] || PALETTE[i % PALETTE.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v, name) => [v, name]} />
                    <Legend formatter={(name) => {
                      const d = sbData.find((s) => s.name === name);
                      return `${name} (${d?.value ?? 0})`;
                    }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
          </Grid>
        </Grid>

        {/* â”€â”€ 5. Top Prescribed Medicines â”€â”€ */}
        <Box sx={{ mb: 3 }}>
          <ChartCard title="Top 10 Most Prescribed Medicines"
            height={Math.max(260, medData.length * 44 + 60)}>
            {medData.length === 0
              ? <EmptyState message="No prescriptions in selected period" />
              : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={medData} layout="vertical"
                    margin={{ top: 5, right: 60, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="medicine" width={190} tick={{ fontSize: 11 }} />
                    <Tooltip content={<CountTooltip />} />
                    <Bar dataKey="Count" fill="#e65100" radius={[0, 4, 4, 0]}
                      label={{ position: 'right', fontSize: 11 }} />
                  </BarChart>
                </ResponsiveContainer>
              )}
          </ChartCard>
        </Box>

        {/* â”€â”€ 6. Doctor-wise Comparison Table â”€â”€ */}
        <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Doctor-wise Comparison</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#e3f2fd' }}>
                  <TableCell sx={{ fontWeight: 700 }}>Doctor</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Specialization</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">Total Patients</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">OPD</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">IPD</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">Prescriptions</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Revenue</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {comparison.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      No data for selected period
                    </TableCell>
                  </TableRow>
                ) : comparison.map((d) => {
                  const isSelected = String(d.doctorId) === String(selectedDoctorId);
                  return (
                    <TableRow key={d.doctorId} hover
                      sx={{ bgcolor: isSelected ? '#e3f2fd' : 'inherit', cursor: 'pointer' }}
                      onClick={() => setSelectedDoctorId(isSelected ? '' : String(d.doctorId))}>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {isSelected && (
                          <Box component="span" sx={{ color: '#1565c0', mr: 0.5, fontSize: 12 }}>â–¶</Box>
                        )}
                        Dr. {d.doctorName}
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontSize: 13 }}>
                        {d.specialization}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={d.totalPatients}
                          size="small"
                          sx={{ bgcolor: '#1565c022', color: '#1565c0', fontWeight: 700, minWidth: 36 }}
                        />
                      </TableCell>
                      <TableCell align="center">{d.opd}</TableCell>
                      <TableCell align="center">{d.ipd}</TableCell>
                      <TableCell align="center">{d.prescriptions}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {fmtCurrency(d.revenue)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Click a row to filter all charts by that doctor. Click again to deselect.
          </Typography>
        </Paper>

        {/* â”€â”€ 7. Recent Appointments â”€â”€ */}
        <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Recent Appointments</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#e3f2fd' }}>
                  <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Patient</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Doctor</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Date &amp; Time</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recentAppts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      No appointments in the selected period
                    </TableCell>
                  </TableRow>
                ) : recentAppts.map((a) => (
                  <TableRow key={a.id} hover>
                    <TableCell sx={{ color: 'text.secondary', fontSize: 13 }}>#{a.id}</TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>{a.patientName}</TableCell>
                    <TableCell>Dr. {a.doctorName}</TableCell>
                    <TableCell>
                      {new Date(a.date + 'T00:00:00').toLocaleDateString('en-IN', {
                        day: '2-digit', month: 'short', year: 'numeric',
                      })}
                      {a.time ? ` ${a.time}` : ''}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={a.type}
                        size="small"
                        sx={{
                          bgcolor: (TYPE_COLORS[a.type] || '#888') + '22',
                          color:    TYPE_COLORS[a.type] || '#555',
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={a.status}
                        size="small"
                        sx={{
                          bgcolor: (STATUS_COLORS[a.status] || '#888') + '22',
                          color:    STATUS_COLORS[a.status] || '#555',
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

      </Container>
    </div>
  );
};

export default DoctorPerformanceDashboard;
